import { track } from '@/lib/analytics/track';
import { TrackingEventName, type TrackingEventV2 } from '@/models/analytics/eventTypes';
import { logger } from '@/utils/logger/logger';
import {
  type PatientDeleteAnalyticsEvent,
  type PatientDeleteEventName,
  type PatientDialogAction,
} from '@heidi/patients/contracts';
import { sanitizeError } from '@heidi/runtime/logger';

/**
 * The app-side emit host for the shared-patient-directory dialogs (COLAB-427):
 * `patient_delete_clicked`, `patient_delete_blocked` and
 * `patient_directory_enable_clicked`.
 *
 * Ruling 24: the dialogs decide WHEN each event fires and supply the payload;
 * this module adds the destination routing and the app union, and nothing else.
 * The names are declared once, in `@heidi/patients/contributions`; this file is
 * the one place that knows they correspond to `TrackingEventV2` members.
 *
 * ROUTING. Snowplow only: every call stamps `options: { amplitude: false }`.
 * Snowplow forwards to Amplitude, so a direct Amplitude send would count each event twice.
 *
 * NO `workspace_id`. The tracker attaches the `workspace_context` entity from
 * `getWorkspaceId()` on every event, and the schemas set
 * `additionalProperties: false`, so a `workspace_id` prop would fail Iglu
 * validation. Nothing here reads or forwards one.
 *
 * NEVER THROWS, and never logs the caught value. Instrumentation must not break
 * the dialog or the delete. The failure is logged through `sanitizeError`, which
 * keeps a status and a code and drops the message: the props carry patient ids,
 * and a library error message can embed them.
 */

/**
 * The token map that binds the two delete events' literal names to the app enum.
 *
 * TOTAL BY CONSTRUCTION: `satisfies Record<PatientDeleteEventName,
 * TrackingEventName>` requires an entry for every member of the port's union, so
 * widening `PatientDeleteAnalyticsEvent` without a `TrackingEventName` member to
 * bind it to fails HERE rather than at the first emit in production. The
 * sibling suite asserts the other direction: each entry's runtime string equals
 * the descriptor's frozen `name`.
 */
const PATIENT_DELETE_EVENT_NAMES = {
  patient_delete_clicked: TrackingEventName.PatientDeleteClicked,
  patient_delete_blocked: TrackingEventName.PatientDeleteBlocked,
} satisfies Record<PatientDeleteEventName, TrackingEventName>;

const emitSnowplowOnly = (event: TrackingEventV2): void => {
  try {
    track({ ...event, options: { amplitude: false } });
  } catch (error) {
    logger.warn('[patient-directory] analytics emit failed', sanitizeError(error));
  }
};

/**
 * `EmitPatientDeleteEvent` for the delete dialog's three composition hosts.
 *
 * THE ONE CAST, and what makes it sound. `event` has already been checked as a
 * discriminated union, so its name/props pairing is proven at the call site; the
 * map then swaps the literal name for the enum member with the identical runtime
 * value. TypeScript cannot follow that the swap preserves the pairing, so the
 * composed object is asserted as a whole, and the pairing is pinned by the
 * union in `/contracts` plus this module's suite.
 */
export const emitPatientDeleteEvent = (event: PatientDeleteAnalyticsEvent): void => {
  emitSnowplowOnly({
    event_name: PATIENT_DELETE_EVENT_NAMES[event.event_name],
    props: event.props,
  } as TrackingEventV2);
};

/**
 * Reports the admin resolving the "Collaborate on patients" dialog. The dialog
 * has one entry point, so `entry_point` is fixed here rather than passed in.
 */
export const trackPatientDirectoryEnableClicked = (action: PatientDialogAction): void => {
  emitSnowplowOnly({
    event_name: TrackingEventName.PatientDirectoryEnableClicked,
    props: { entry_point: 'workspace_settings', action },
  });
};
